import React, { useEffect, useState, useCallback } from "react";
import { Loader2, LifeBuoy, Plus, X, ExternalLink, Phone, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CATEGORIES = ["treatment", "meetings", "family", "reading", "podcasts", "other"];
const CAT_COLOR = {
  treatment: "bg-primary/15 text-primary", meetings: "bg-seafoam/15 text-seafoam",
  family: "bg-brass/20 text-brass", reading: "bg-warning/15 text-warning",
  podcasts: "bg-primary/15 text-primary", other: "bg-secondary text-muted-foreground",
};

const AddResourceDialog = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({ category: "treatment", title: "", description: "", url: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const up = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setBusy(true);
    try {
      await api.post("/community-v2/resources/suggest", {
        category: form.category, title: form.title.trim(),
        description: form.description.trim() || null, url: form.url.trim() || null, phone: form.phone.trim() || null,
      });
      toast.success("Resource shared with the community");
      onCreated(); onClose();
    } catch (err) { toast.error(errMsg(err, "Could not add resource")); }
    finally { setBusy(false); }
  };

  const inp = "w-full px-3 py-2.5 rounded-lg border border-input bg-secondary/40 outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-nightsea/75 backdrop-blur-sm" onClick={onClose} />
      <form onSubmit={submit} className="relative w-full max-w-md bg-card border border-border rounded-2xl p-6 animate-fade-up">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-muted-foreground"><X className="w-5 h-5" /></button>
        <h3 className="font-heading text-xl font-semibold mb-1">Share a resource</h3>
        <p className="text-sm text-muted-foreground mb-4">Help others by sharing something that helped you.</p>
        <div className="space-y-3">
          <input data-testid="resource-title" className={inp} placeholder="Title" value={form.title} onChange={up("title")} />
          <select data-testid="resource-category" className={inp + " capitalize"} value={form.category} onChange={up("category")}>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <textarea data-testid="resource-description" className={inp + " resize-none"} rows={3} placeholder="What is it and how does it help?" value={form.description} onChange={up("description")} />
          <input data-testid="resource-url" className={inp} placeholder="Website / link (optional)" value={form.url} onChange={up("url")} />
          <input data-testid="resource-phone" className={inp} placeholder="Phone (optional)" value={form.phone} onChange={up("phone")} />
        </div>
        <button data-testid="resource-create-submit" type="submit" disabled={busy || !form.title.trim()}
          className="mt-5 w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground font-semibold py-2.5 rounded-full disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Share resource
        </button>
      </form>
    </div>
  );
};

const Resources = () => {
  const { user } = useAuth();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/community-v2/resources"); setResources(data.resources || []); }
    catch (e) { toast.error("Could not load resources"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Remove this resource?")) return;
    try { await api.delete(`/community-v2/resources/${id}`); toast.success("Resource removed"); load(); }
    catch (e) { toast.error(errMsg(e, "Could not remove")); }
  };

  // Backend list response omits submitted_by; fall back to name/alias match.
  const isMine = (r) =>
    (r.submitted_by && r.submitted_by === user?.id) ||
    (!!r.submitted_by_name && (r.submitted_by_name === user?.name || r.submitted_by_name === user?.display_alias));

  const filtered = resources.filter((r) =>
    (cat === "all" || r.category === cat) &&
    (!q || (r.title + " " + (r.description || "")).toLowerCase().includes(q.toLowerCase()))
  );

  return (
    <div className="px-4 sm:px-6 py-8">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-semibold flex items-center gap-2"><LifeBuoy className="w-7 h-7 text-primary" /> Resources</h1>
            <p className="text-muted-foreground mt-2">Helpful services shared by the community — add your own too.</p>
          </div>
          <button data-testid="add-resource-button" onClick={() => setShow(true)}
            className="flex items-center gap-2 text-sm font-medium bg-primary text-primary-foreground rounded-full px-4 py-2 hover:bg-atlantic transition-colors">
            <Plus className="w-4 h-4" /> Add
          </button>
        </div>

        <div className="relative mb-4">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="resource-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search resources…"
            className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-input bg-secondary/40 outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
        </div>
        <div className="flex flex-wrap gap-1.5 mb-6">
          {["all", ...CATEGORIES].map((c) => (
            <button key={c} data-testid={`resource-cat-${c}`} onClick={() => setCat(c)}
              className={`px-3 py-1.5 rounded-full text-sm capitalize transition-colors ${cat === c ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground hover:bg-secondary"}`}>
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <LifeBuoy className="w-8 h-8 mx-auto mb-3 opacity-40" />
            <p>No resources here yet. Share one to help a fellow traveller.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="resources-list">
            {filtered.map((r) => (
              <div key={r.id} data-testid="resource-item" className="bg-card border border-border rounded-2xl p-5 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium capitalize ${CAT_COLOR[r.category] || CAT_COLOR.other}`}>{r.category}</span>
                    <h3 className="font-semibold">{r.title}</h3>
                  </div>
                  {r.description && <p className="text-sm text-foreground/80 mt-2 leading-relaxed">{r.description}</p>}
                  <div className="flex items-center gap-4 mt-2 text-sm">
                    {r.url && <a data-testid="resource-link" href={r.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-primary hover:underline"><ExternalLink className="w-3.5 h-3.5" /> Visit</a>}
                    {r.phone && <a href={`tel:${r.phone}`} className="flex items-center gap-1 text-muted-foreground hover:text-foreground"><Phone className="w-3.5 h-3.5" /> {r.phone}</a>}
                  </div>
                  {r.submitted_by_name && <p className="text-xs text-muted-foreground mt-2">Shared by {r.submitted_by_name}</p>}
                </div>
                {isMine(r) && (
                  <button data-testid={`delete-resource-${r.id}`} onClick={() => del(r.id)} title="Remove"
                    className="w-9 h-9 rounded-full bg-secondary hover:bg-destructive/20 text-destructive flex items-center justify-center shrink-0"><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {show && <AddResourceDialog onClose={() => setShow(false)} onCreated={load} />}
    </div>
  );
};

export default Resources;
